import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, AppBar, Box, Breadcrumbs, Button, Card, CardActionArea, Chip, CircularProgress, Container,
  CssBaseline, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton,
  Checkbox, InputAdornment, LinearProgress, Link, ListItemIcon, Menu, MenuItem, Paper, Snackbar, Stack,
  SwipeableDrawer, Fab, List, ListItemButton, ListItemText, useMediaQuery,
  TextField, ThemeProvider, ToggleButton, ToggleButtonGroup, Toolbar, Tooltip, Typography, createTheme,
} from "@mui/material";
import {
  Add, ArrowBack, Cloud, CloudUpload, Close, CreateNewFolder, Delete, Description, Download,
  DriveFileRenameOutline, Folder, Image as ImageIcon, InsertDriveFile, Movie, MoreVert, MusicNote,
  NavigateNext, PictureAsPdf, IosShare, AddBoxOutlined, SaveAlt, CheckCircle, RadioButtonUnchecked, Checklist, DriveFolderUpload, ViewModule, ViewList, Refresh, Search, Visibility, Logout, Lock, Person, DeleteSweep, SelectAll, Deselect,
} from "@mui/icons-material";

import ImageViewer, { ViewerImage } from "./ImageViewer";
import { isIOSSafari, isMobile, isStandalone, saveFile, SaveEvent } from "./app/save";
import { fetchPath, processTransferTask } from "./app/transfer";
import { FileItem, TransferTask, baseName, encodeKey, isDirectory } from "./app/types";
import { formatDate, humanReadableSize } from "./app/utils";

const API = "/webdav/";
const HIDDEN = "_$flaredrive$";

const theme = createTheme({
  palette: { primary: { main: "#2563eb" }, secondary: { main: "#f38020" }, background: { default: "#f5f7fb" } },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: '"PingFang SC","Microsoft YaHei","Noto Sans CJK SC","Noto Sans SC","Helvetica Neue",Arial,sans-serif',
    button: { textTransform: "none", fontWeight: 600 },
  },
  components: { MuiButton: { defaultProps: { disableElevation: true } } },
});

type Kind = "image" | "video" | "audio" | "pdf" | "text" | "other";
function kindOf(f: FileItem): Kind {
  const t = f.httpMetadata?.contentType || "";
  const ext = baseName(f.key).split(".").pop()?.toLowerCase() || "";
  if (t.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext)) return "image";
  if (t.startsWith("video/") || ["mp4", "webm", "mov"].includes(ext)) return "video";
  if (t.startsWith("audio/") || ["mp3", "wav", "m4a", "flac", "ogg"].includes(ext)) return "audio";
  if (t === "application/pdf" || ext === "pdf") return "pdf";
  if (t.startsWith("text/") || t === "application/json" ||
    ["txt", "md", "csv", "json", "log", "js", "ts", "py", "html", "css", "xml", "yaml", "yml", "ini"].includes(ext)) return "text";
  return "other";
}
const KIND_ICON: Record<Kind, React.ReactElement> = {
  image: <ImageIcon sx={{ color: "#10b981" }} />, video: <Movie sx={{ color: "#8b5cf6" }} />,
  audio: <MusicNote sx={{ color: "#ec4899" }} />, pdf: <PictureAsPdf sx={{ color: "#ef4444" }} />,
  text: <Description sx={{ color: "#3b82f6" }} />, other: <InsertDriveFile sx={{ color: "#64748b" }} />,
};
const fileUrl = (key: string) => `${API}${encodeKey(key)}`;

async function davMkcol(key: string) {
  const r = await fetch(fileUrl(key), { method: "MKCOL" });
  if (r.status === 405) throw new Error("已经有同名的分类或文件夹了");
  if (!r.ok) throw new Error(`创建失败（${r.status}）`);
}
async function davMove(src: string, dst: string) {
  const r = await fetch(fileUrl(src), {
    method: "MOVE",
    headers: { Destination: new URL(fileUrl(dst), window.location.href).href, Overwrite: "F" },
  });
  if (r.status === 412) throw new Error("已经存在同名项目，请换一个名字");
  if (!r.ok) throw new Error(`重命名失败（${r.status}）`);
}
async function davDelete(key: string) {
  const r = await fetch(fileUrl(key), { method: "DELETE" });
  if (!r.ok && r.status !== 404) throw new Error(`删除失败（${r.status}）`);
}
function validName(n: string) {
  if (!n.trim()) return "名称不能为空";
  if (/[/\\]/.test(n)) return "名称里不能包含 / 或 \\";
  if (n.startsWith(HIDDEN)) return "这个名称是系统保留的";
  return "";
}

/* ---------- 文件夹上传 ---------- */
type UploadEntry = { file: File; path: string };
const JUNK = /^(Thumbs\.db|desktop\.ini|\.DS_Store)$/i;
function readAllEntries(reader: any): Promise<any[]> {
  // readEntries 每次最多返回 100 个，需要循环读取
  return new Promise((resolve, reject) => {
    const out: any[] = [];
    const step = () => reader.readEntries((batch: any[]) => { if (!batch.length) resolve(out); else { out.push(...batch); step(); } }, reject);
    step();
  });
}
async function walkEntry(entry: any, base: string, out: UploadEntry[]) {
  if (entry.isFile) {
    const file: File = await new Promise((res, rej) => entry.file(res, rej));
    out.push({ file, path: base + file.name });
  } else if (entry.isDirectory) {
    const children = await readAllEntries(entry.createReader());
    for (const c of children) await walkEntry(c, `${base}${entry.name}/`, out);
  }
}
async function readDrop(dt: DataTransfer): Promise<UploadEntry[]> {
  const items = Array.from(dt.items || []).filter((i) => i.kind === "file");
  const entries = items.map((i) => (i as any).webkitGetAsEntry?.()).filter(Boolean);
  if (!entries.length) return Array.from(dt.files).map((file) => ({ file, path: file.name }));
  const out: UploadEntry[] = [];
  for (const e of entries) await walkEntry(e, "", out);
  return out;
}

/* ---------- 通用对话框 ---------- */
function NameDialog({ open, title, label, initial, okText, onClose, onSubmit }: {
  open: boolean; title: string; label: string; initial?: string; okText: string;
  onClose: () => void; onSubmit: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState(initial || "");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setName(initial || ""); setErr(""); } }, [open, initial]);
  const submit = async () => {
    const v = validName(name);
    if (v) return setErr(v);
    setBusy(true);
    try { await onSubmit(name.trim()); onClose(); } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth margin="dense" label={label} value={name} error={!!err}
          helperText={err || "例如：照片、文档、工作资料"}
          onChange={(e) => { setName(e.target.value); setErr(""); }}
          onKeyDown={(e) => e.key === "Enter" && submit()} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">取消</Button>
        <Button variant="contained" onClick={submit} disabled={busy}>{busy ? "请稍候…" : okText}</Button>
      </DialogActions>
    </Dialog>
  );
}
function ConfirmDialog({ state, onClose }: {
  state: { title: string; text: string; ok: string; action: () => Promise<void> } | null; onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={!!state} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>{state?.title}</DialogTitle>
      <DialogContent><DialogContentText>{state?.text}</DialogContentText></DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">取消</Button>
        <Button variant="contained" color="error" disabled={busy}
          onClick={async () => { setBusy(true); try { await state?.action(); } finally { setBusy(false); onClose(); } }}>
          {busy ? "正在处理…" : state?.ok}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ---------- 预览 ---------- */
function PreviewDialog({ file, onClose }: { file: FileItem | null; onClose: () => void }) {
  const [text, setText] = useState<string | null>(null);
  const kind = file ? kindOf(file) : "other";
  useEffect(() => {
    setText(null);
    if (file && kind === "text")
      fetch(fileUrl(file.key)).then((r) => r.text()).then((t) => setText(t.slice(0, 200000))).catch(() => setText("（无法读取文件内容）"));
  }, [file, kind]);
  if (!file) return null;
  const url = fileUrl(file.key);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { height: kind === "pdf" ? "90vh" : undefined } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
        {KIND_ICON[kind]}
        <Typography variant="h6" noWrap sx={{ flex: 1, fontWeight: 700 }}>{baseName(file.key)}</Typography>
        <Button startIcon={<Download />} onClick={() => saveFile(file.key, baseName(file.key), file.httpMetadata?.contentType)}>下载</Button>
        <IconButton onClick={onClose} aria-label="关闭"><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: "flex", justifyContent: "center", alignItems: "center", bgcolor: "#f8fafc", p: kind === "pdf" ? 0 : 2 }}>
        {kind === "image" && <img src={url} alt={baseName(file.key)} style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain" }} />}
        {kind === "video" && <video src={url} controls autoPlay style={{ maxWidth: "100%", maxHeight: "75vh" }} />}
        {kind === "audio" && <audio src={url} controls autoPlay />}
        {kind === "pdf" && <iframe src={url} title="PDF 预览" style={{ border: 0, width: "100%", height: "100%" }} />}
        {kind === "text" && (text === null ? <CircularProgress /> :
          <Box component="pre" sx={{ m: 0, width: "100%", whiteSpace: "pre-wrap", wordBreak: "break-all", fontSize: 14, fontFamily: "monospace" }}>{text}</Box>)}
        {kind === "other" && (
          <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
            <InsertDriveFile sx={{ fontSize: 64, color: "#94a3b8" }} />
            <Typography color="text.secondary">这种文件暂时不支持在线预览，可以下载后查看</Typography>
            <Button variant="contained" startIcon={<Download />} href={url} download={baseName(file.key)}>下载文件</Button>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- 卡片 ---------- */
function ItemMenu({ onRename, onDelete, onDownload, downloadLabel = "下载" }: { onRename: () => void; onDelete: () => void; onDownload?: () => void; downloadLabel?: string }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);
  return (
    <>
      <Tooltip title="更多操作"><IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}><MoreVert /></IconButton></Tooltip>
      <Menu anchorEl={anchor} open={!!anchor} onClose={close} onClick={(e) => e.stopPropagation()}>
        {onDownload && <MenuItem onClick={() => { close(); onDownload(); }}><ListItemIcon>{downloadLabel === "下载" ? <Download fontSize="small" /> : <SaveAlt fontSize="small" />}</ListItemIcon>{downloadLabel}</MenuItem>}
        <MenuItem onClick={() => { close(); onRename(); }}><ListItemIcon><DriveFileRenameOutline fontSize="small" /></ListItemIcon>重命名</MenuItem>
        <MenuItem onClick={() => { close(); onDelete(); }} sx={{ color: "error.main" }}><ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>删除</MenuItem>
      </Menu>
    </>
  );
}
const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444", "#06b6d4", "#ec4899"];
function SelectBox({ checked, onToggle }: { checked: boolean; onToggle: () => void }) {
  return (
    <Checkbox checked={checked} onClick={(e) => e.stopPropagation()} onChange={onToggle}
      inputProps={{ "aria-label": "选择" }}
      sx={{ position: "absolute", top: 6, left: 6, zIndex: 2, bgcolor: "rgba(255,255,255,.85)", borderRadius: 2, p: 0.5, "&:hover": { bgcolor: "#fff" } }} />
  );
}
type SelProps = { selected: boolean; onToggle: () => void };
function CategoryCard({ item, onOpen, onRename, onDelete, selected, onToggle }: SelProps & { item: FileItem; onOpen: () => void; onRename: () => void; onDelete: () => void }) {
  const name = baseName(item.key);
  const color = COLORS[[...name].reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length];
  return (
    <Card variant="outlined" sx={{ position: "relative", transition: ".15s", borderColor: selected ? "primary.main" : undefined, outline: selected ? "2px solid" : "none", outlineColor: "primary.main", "&:hover": { boxShadow: 4, transform: "translateY(-2px)" } }}>
      <SelectBox checked={selected} onToggle={onToggle} />
      <CardActionArea onClick={onOpen} sx={{ p: 2.5, pt: 5 }}>
        <Box sx={{ width: 56, height: 56, borderRadius: 3, bgcolor: color + "1f", display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5 }}>
          <Folder sx={{ fontSize: 34, color }} />
        </Box>
        <Typography variant="h6" noWrap sx={{ fontWeight: 700, pr: 3 }}>{name}</Typography>
        <Typography variant="body2" color="text.secondary">创建于 {formatDate(item.uploaded)}</Typography>
      </CardActionArea>
      <Box sx={{ position: "absolute", top: 8, right: 8 }}><ItemMenu onRename={onRename} onDelete={onDelete} /></Box>
    </Card>
  );
}
function useDownload(item: FileItem) {
  return () => { saveFile(item.key, baseName(item.key), item.httpMetadata?.contentType); };
}
const saveLabel = (item: FileItem) => (kindOf(item) === "image" && isMobile() ? "保存到相册" : "下载");
function Thumb({ item, big }: { item: FileItem; big?: boolean }) {
  const kind = kindOf(item);
  const thumb = item.customMetadata?.thumbnail;
  // 小于 8MB 的图片直接显示原图，清晰度更好；其他使用上传时生成的缩略图
  const src = kind === "image" && item.size < 8 * 1024 * 1024 ? fileUrl(item.key)
    : thumb ? `${API}${HIDDEN}/thumbnails/${thumb}.png` : null;
  const icon = KIND_ICON[kind];
  return src
    ? <img src={src} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
    : React.cloneElement(icon, { sx: { ...icon.props.sx, fontSize: big ? 64 : 28 } });
}
const KIND_LABEL: Record<Kind, string> = { image: "图片", video: "视频", audio: "音频", pdf: "PDF", text: "文本", other: "文件" };
function FileCard({ item, onOpen, onRename, onDelete, selected, onToggle }: SelProps & { item: FileItem; onOpen: () => void; onRename: () => void; onDelete: () => void }) {
  const kind = kindOf(item);
  const download = useDownload(item);
  const name = baseName(item.key);
  return (
    <Card variant="outlined" sx={{ position: "relative", display: "flex", flexDirection: "column", transition: ".15s", borderColor: selected ? "primary.main" : undefined, outline: selected ? "2px solid" : "none", outlineColor: "primary.main", "&:hover": { boxShadow: 5, transform: "translateY(-2px)" } }}>
      <SelectBox checked={selected} onToggle={onToggle} />
      <Chip size="small" label={KIND_LABEL[kind]} sx={{ position: "absolute", top: 10, right: 10, zIndex: 2, bgcolor: "rgba(15,23,42,.6)", color: "#fff", fontWeight: 600 }} />
      <CardActionArea onClick={onOpen}>
        <Box sx={{ height: 190, p: 1.5, bgcolor: "#eef2f7", backgroundImage: "linear-gradient(45deg,#e8edf4 25%,transparent 25%,transparent 75%,#e8edf4 75%),linear-gradient(45deg,#e8edf4 25%,transparent 25%,transparent 75%,#e8edf4 75%)", backgroundSize: "20px 20px", backgroundPosition: "0 0,10px 10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Thumb item={item} big />
        </Box>
      </CardActionArea>
      <Box sx={{ p: 1.5, pb: 0.5, flex: 1, cursor: "pointer" }} onClick={onOpen}>
        <Tooltip title={name} placement="top" enterDelay={400}>
          <Typography sx={{ fontWeight: 600, lineHeight: 1.35, minHeight: "2.7em", wordBreak: "break-all", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{name}</Typography>
        </Tooltip>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>{humanReadableSize(item.size)} · {formatDate(item.uploaded)}</Typography>
      </Box>
      <Stack direction="row" alignItems="center" sx={{ px: 1, pb: 1, borderTop: "1px solid #f1f5f9", pt: 0.5 }}>
        <Button size="small" startIcon={<Visibility />} onClick={onOpen}>预览</Button>
        <Button size="small" startIcon={<Download />} onClick={download} color="inherit">下载</Button>
        <Box sx={{ flex: 1 }} />
        <ItemMenu onRename={onRename} onDelete={onDelete} onDownload={download} downloadLabel={saveLabel(item)} />
      </Stack>
    </Card>
  );
}
function FileRow({ item, onOpen, onRename, onDelete, selected, onToggle }: SelProps & { item: FileItem; onOpen: () => void; onRename: () => void; onDelete: () => void }) {
  const download = useDownload(item);
  const name = baseName(item.key);
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 1, py: 1, borderBottom: "1px solid #eef2f7", bgcolor: selected ? "#eff6ff" : "#fff", "&:hover": { bgcolor: selected ? "#e0edff" : "#f8fafc" } }}>
      <Checkbox checked={selected} onChange={onToggle} inputProps={{ "aria-label": "选择" }} />
      <Box onClick={onOpen} sx={{ width: 48, height: 48, flexShrink: 0, borderRadius: 2, overflow: "hidden", bgcolor: "#eef2f7", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
        <Thumb item={item} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={onOpen}>
        <Tooltip title={name} placement="top-start" enterDelay={400}><Typography noWrap sx={{ fontWeight: 600 }}>{name}</Typography></Tooltip>
        <Typography variant="caption" color="text.secondary" sx={{ display: { sm: "none" } }}>{humanReadableSize(item.size)} · {formatDate(item.uploaded)}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ width: 90, textAlign: "right", display: { xs: "none", sm: "block" } }}>{humanReadableSize(item.size)}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ width: 170, display: { xs: "none", md: "block" } }}>{formatDate(item.uploaded)}</Typography>
      <Tooltip title="预览"><IconButton onClick={onOpen}><Visibility /></IconButton></Tooltip>
      <Tooltip title="下载"><IconButton onClick={download}><Download /></IconButton></Tooltip>
      <ItemMenu onRename={onRename} onDelete={onDelete} onDownload={download} downloadLabel={saveLabel(item)} />
    </Stack>
  );
}
/* ---------- 手机端紧凑视图 ---------- */
function useLongPress(cb: () => void) {
  const t = useRef<any>(null);
  return {
    onTouchStart: () => { t.current = setTimeout(() => { t.current = "fired"; cb(); }, 500); },
    onTouchMove: () => { if (t.current !== "fired") clearTimeout(t.current); t.current = null; },
    onTouchEnd: (e: React.TouchEvent) => { if (t.current === "fired") e.preventDefault(); else clearTimeout(t.current); t.current = null; },
  };
}
function MobileTile({ item, selectMode, selected, onToggle, onOpen, onRename, onDelete, onLongPress }: SelProps & {
  item: FileItem; selectMode: boolean; onOpen: () => void; onRename: () => void; onDelete: () => void; onLongPress: () => void;
}) {
  const kind = kindOf(item);
  const download = useDownload(item);
  const lp = useLongPress(onLongPress);
  const thumb = item.customMetadata?.thumbnail;
  const src = kind === "image" && item.size < 8 * 1024 * 1024 ? fileUrl(item.key) : thumb ? `${API}${HIDDEN}/thumbnails/${thumb}.png` : null;
  const media = kind === "image" || kind === "video";
  return (
    <Box {...lp} onClick={() => (selectMode ? onToggle() : onOpen())} onContextMenu={(e) => e.preventDefault()}
      sx={{ position: "relative", minWidth: 0, aspectRatio: "1 / 1", bgcolor: media ? "#e2e8f0" : "#fff", overflow: "hidden", borderRadius: 1.5,
        border: media ? "none" : "1px solid #e5e7eb", cursor: "pointer", WebkitTouchCallout: "none",
        outline: selected ? "3px solid #2563eb" : "none", outlineOffset: -3 }}>
      {src ? <img src={src} alt="" loading="lazy" draggable={false} style={{ width: "100%", height: "100%", objectFit: media ? "cover" : "contain", display: "block", transform: selected ? "scale(.92)" : undefined, transition: "transform .15s" }} />
        : <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", pb: 3 }}>{React.cloneElement(KIND_ICON[kind], { sx: { ...KIND_ICON[kind].props.sx, fontSize: 40 } })}</Stack>}
      <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, px: 0.75, pt: 2, pb: 0.5, color: media ? "#fff" : "text.primary",
        background: media ? "linear-gradient(transparent, rgba(0,0,0,.6))" : "none" }}>
        <Typography noWrap sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1.3 }}>{baseName(item.key)}</Typography>
        <Typography noWrap sx={{ fontSize: 10, opacity: 0.85, lineHeight: 1.2 }}>{humanReadableSize(item.size)}</Typography>
      </Box>
      {selectMode ? (
        <Box sx={{ position: "absolute", top: 4, right: 4, color: selected ? "#2563eb" : "#fff", filter: "drop-shadow(0 0 2px rgba(0,0,0,.5))", lineHeight: 0, bgcolor: selected ? "#fff" : "transparent", borderRadius: "50%" }}>
          {selected ? <CheckCircle sx={{ fontSize: 22 }} /> : <RadioButtonUnchecked sx={{ fontSize: 22 }} />}
        </Box>
      ) : (
        <Box sx={{ position: "absolute", top: 2, right: 2, "& .MuiIconButton-root": { color: media ? "#fff" : "#475569", bgcolor: media ? "rgba(0,0,0,.25)" : "transparent", p: 0.25 } }} onClick={(e) => e.stopPropagation()}>
          <ItemMenu onRename={onRename} onDelete={onDelete} onDownload={download} downloadLabel={saveLabel(item)} />
        </Box>
      )}
    </Box>
  );
}
function MobileFolder({ item, selectMode, selected, onToggle, onOpen, onRename, onDelete, onLongPress }: SelProps & {
  item: FileItem; selectMode: boolean; onOpen: () => void; onRename: () => void; onDelete: () => void; onLongPress: () => void;
}) {
  const name = baseName(item.key);
  const color = COLORS[[...name].reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length];
  const lp = useLongPress(onLongPress);
  return (
    <Paper variant="outlined" {...lp} onClick={() => (selectMode ? onToggle() : onOpen())} onContextMenu={(e) => e.preventDefault()}
      sx={{ minWidth: 0, overflow: "hidden", display: "flex", alignItems: "center", gap: 1, p: 1.25, pr: 0.25, cursor: "pointer", borderColor: selected ? "primary.main" : undefined, bgcolor: selected ? "#eff6ff" : "#fff", WebkitTouchCallout: "none" }}>
      <Box sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: 2, bgcolor: color + "1f", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Folder sx={{ fontSize: 22, color }} />
      </Box>
      <Typography noWrap sx={{ fontWeight: 600, flex: 1, minWidth: 0, fontSize: 14 }}>{name}</Typography>
      {selectMode ? (selected ? <CheckCircle color="primary" sx={{ mr: 1 }} /> : <RadioButtonUnchecked sx={{ mr: 1, color: "#94a3b8" }} />)
        : <Box onClick={(e) => e.stopPropagation()}><ItemMenu onRename={onRename} onDelete={onDelete} /></Box>}
    </Paper>
  );
}

function CardGrid({ children, min = 220 }: { children: React.ReactNode; min?: number }) {
  return <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: `repeat(auto-fill, minmax(min(${min}px, 100%), 1fr))` }}>{children}</Box>;
}
function EmptyState({ title, hint, action }: { title: string; hint: string; action?: React.ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ py: 6, px: 3, textAlign: "center", borderStyle: "dashed", borderWidth: 2, bgcolor: "#fafcff" }}>
      <CloudUpload sx={{ fontSize: 64, color: "#93c5fd" }} />
      <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>{title}</Typography>
      <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>{hint}</Typography>
      {action}
    </Paper>
  );
}

/* ---------- 上传进度 ---------- */
function UploadPanel({ tasks, onClear }: { tasks: TransferTask[]; onClear: () => void }) {
  if (!tasks.length) return null;
  const done = tasks.filter((t) => t.status === "completed").length;
  const failed = tasks.filter((t) => t.status === "failed").length;
  const running = tasks.some((t) => t.status === "pending" || t.status === "in-progress");
  return (
    <Paper elevation={8} sx={{ position: "fixed", right: { xs: 8, sm: 24 }, bottom: { xs: 88, sm: 24 }, width: 340, maxWidth: { xs: "calc(100vw - 16px)", sm: "calc(100vw - 48px)" }, p: 2, zIndex: 1300 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography sx={{ fontWeight: 700, flex: 1 }}>
          {running ? `正在上传（${done}/${tasks.length}）` : failed ? `上传完成，${failed} 个失败` : `全部上传完成（${done} 个文件）`}
        </Typography>
        {!running && <IconButton size="small" onClick={onClear} aria-label="关闭"><Close fontSize="small" /></IconButton>}
      </Stack>
      <Stack spacing={1} sx={{ maxHeight: 220, overflow: "auto" }}>
        {tasks.map((t, i) => (
          <Box key={i}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>{t.name}</Typography>
              <Typography variant="caption" color={t.status === "failed" ? "error" : "text.secondary"}>
                {t.status === "completed" ? "已完成" : t.status === "failed" ? "失败" : t.status === "pending" ? "等待中" : `${Math.round((t.loaded / (t.total || 1)) * 100)}%`}
              </Typography>
            </Stack>
            <LinearProgress variant="determinate" color={t.status === "failed" ? "error" : t.status === "completed" ? "success" : "primary"}
              value={t.status === "completed" ? 100 : (t.loaded / (t.total || 1)) * 100} sx={{ height: 6, borderRadius: 3 }} />
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

/* ---------- 主界面 ---------- */
function getCwd() { return decodeURIComponent(window.location.hash.replace(/^#\/?/, "")); }

function Drive({ username, onLogout }: { username: string; onLogout: () => void }) {
  const [cwd, setCwdState] = useState(getCwd());
  const [items, setItems] = useState<FileItem[] | null>(null);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState<{ msg: string; sev: "success" | "error" } | null>(null);
  const [nameDlg, setNameDlg] = useState<null | { title: string; label: string; initial?: string; ok: string; submit: (n: string) => Promise<void> }>(null);
  const [confirm, setConfirm] = useState<null | { title: string; text: string; ok: string; action: () => Promise<void> }>(null);
  const [preview, setPreview] = useState<FileItem | null>(null);
  const [tasks, setTasks] = useState<TransferTask[]>([]);
  const [dragging, setDragging] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [view, setView] = useState<"grid" | "list">(() => (localStorage.getItem("fd-view") as any) || "grid");
  useEffect(() => { localStorage.setItem("fd-view", view); }, [view]);
  const mobile = useMediaQuery("(max-width:639.95px)");
  const [selectMode, setSelectMode] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [acctMenu, setAcctMenu] = useState<HTMLElement | null>(null);
  const [viewer, setViewer] = useState<number | null>(null);
  const [bulk, setBulk] = useState<null | { done: number; total: number }>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);

  const setCwd = (p: string) => { window.location.hash = p ? "/" + p : ""; };
  useEffect(() => {
    const h = () => { setCwdState(getCwd()); setSearch(""); };
    window.addEventListener("hashchange", h);
    return () => window.removeEventListener("hashchange", h);
  }, []);

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const list = await fetchPath(cwd);
      setItems(list.filter((f) => !f.key.startsWith(HIDDEN)));
    } catch (e: any) { setItems([]); setLoadError(e.message); }
  }, [cwd]);
  useEffect(() => { setItems(null); setSelected(new Set()); setSelectMode(false); setViewer(null); load(); }, [load]);
  useEffect(() => { document.title = cwd ? `${baseName(cwd)} - 我的网盘` : "我的网盘"; }, [cwd]);

  const ok = (msg: string) => setToast({ msg, sev: "success" });
  const fail = (e: any) => setToast({ msg: e?.message || String(e), sev: "error" });

  const { folders, files } = useMemo(() => {
    const s = search.trim().toLowerCase();
    const list = (items || []).filter((f) => !s || baseName(f.key).toLowerCase().includes(s));
    const byName = (a: FileItem, b: FileItem) => baseName(a.key).localeCompare(baseName(b.key), "zh-CN");
    return { folders: list.filter(isDirectory).sort(byName), files: list.filter((f) => !isDirectory(f)).sort(byName) };
  }, [items, search]);

  const upload = useCallback(async (input: Array<File | UploadEntry>) => {
    const entries: UploadEntry[] = input
      .map((x) => (x instanceof File ? { file: x, path: (x as any).webkitRelativePath || x.name } : x))
      .filter((x) => !JUNK.test(baseName(x.path)));
    if (!entries.length) return;
    const prefix = cwd ? cwd.replace(/\/?$/, "/") : "";
    // 先按层级创建所有需要的文件夹（服务器要求父目录存在）
    const dirs = new Set<string>();
    for (const { path } of entries) {
      const parts = path.split("/").slice(0, -1);
      for (let i = 1; i <= parts.length; i++) dirs.add(parts.slice(0, i).join("/"));
    }
    const sortedDirs = Array.from(dirs).sort((a, b) => a.split("/").length - b.split("/").length);
    for (const d of sortedDirs) {
      const r = await fetch(fileUrl(prefix + d), { method: "MKCOL" });
      if (!r.ok && r.status !== 405) { fail(new Error(`创建文件夹「${d}」失败（${r.status}）`)); return; }
    }
    if (sortedDirs.length) load();
    const newTasks: TransferTask[] = entries.map(({ file, path }) => ({
      type: "upload", name: path, file, remoteKey: prefix + path, loaded: 0, total: file.size, status: "pending",
    }));
    setTasks((t) => [...t.filter((x) => x.status !== "completed" && x.status !== "failed"), ...newTasks]);
    const update = (task: TransferTask, patch: Partial<TransferTask>) => { Object.assign(task, patch); setTasks((t) => [...t]); };
    let next = 0;
    const worker = async () => {
      while (next < newTasks.length) {
        const task = newTasks[next++];
        update(task, { status: "in-progress" });
        try {
          const res = await processTransferTask({ task, onTaskProgress: ({ loaded }) => update(task, { loaded }) });
          if (res && !(res as Response).ok) throw new Error(`HTTP ${(res as Response).status}`);
          update(task, { status: "completed", loaded: task.total });
        } catch (e) { update(task, { status: "failed", error: e }); }
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cwd, load]);

  const pickFolder = () => folderInput.current?.click();
  const pickFiles = () => fileInput.current?.click();
  const newFolder = (isCategory: boolean) => setNameDlg({
    title: isCategory ? "新建分类" : "新建文件夹", label: isCategory ? "分类名称" : "文件夹名称", ok: "创建",
    submit: async (n) => { await davMkcol((cwd ? cwd.replace(/\/?$/, "/") : "") + n); ok(`已创建「${n}」`); load(); },
  });
  const rename = (item: FileItem) => {
    const old = baseName(item.key);
    const dir = isDirectory(item);
    setNameDlg({
      title: dir ? "重命名分类" : "重命名文件", label: "新名称", initial: old, ok: "保存",
      submit: async (n) => {
        if (n === old) return;
        const parent = item.key.slice(0, item.key.length - old.length);
        await davMove(item.key, parent + n); ok("重命名成功"); load();
      },
    });
  };
  const remove = (item: FileItem) => {
    const dir = isDirectory(item);
    const n = baseName(item.key);
    setConfirm({
      title: dir ? "删除分类" : "删除文件",
      text: dir ? `确定要删除分类「${n}」吗？里面的所有文件都会被一起删除，而且无法恢复。` : `确定要删除「${n}」吗？删除后无法恢复。`,
      ok: "确认删除",
      action: async () => { try { await davDelete(item.key); ok("已删除"); load(); } catch (e) { fail(e); } },
    });
  };

  const visible = [...folders, ...files];
  const toggle = (k: string) => setSelected((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const allSelected = visible.length > 0 && visible.every((f) => selected.has(f.key));
  const selectAll = () => setSelected(new Set(visible.map((f) => f.key)));
  const clearSel = () => setSelected(new Set());
  const bulkDelete = () => {
    const keys = Array.from(selected);
    const nDir = (items || []).filter((f) => selected.has(f.key) && isDirectory(f)).length;
    setConfirm({
      title: `删除所选的 ${keys.length} 项`,
      text: `确定要删除已选的 ${keys.length} 项吗？${nDir ? `其中包含 ${nDir} 个分类/文件夹，里面的所有文件也会一起删除。` : ""}删除后无法恢复。`,
      ok: `删除 ${keys.length} 项`,
      action: async () => {
        let okN = 0, failN = 0;
        setBulk({ done: 0, total: keys.length });
        for (const k of keys) {
          try { await davDelete(k); okN++; } catch { failN++; }
          setBulk({ done: okN + failN, total: keys.length });
        }
        setBulk(null); clearSel(); setSelectMode(false); load();
        failN ? fail(new Error(`已删除 ${okN} 项，${failN} 项删除失败`)) : ok(`已删除 ${okN} 项`);
      },
    });
  };
  const images = files.filter((f) => kindOf(f) === "image");
  const viewerImages: ViewerImage[] = images.map((f) => ({ key: f.key, name: baseName(f.key), url: fileUrl(f.key) }));
  const openFile = (f: FileItem) => {
    const i = images.findIndex((x) => x.key === f.key);
    if (i >= 0) setViewer(i); else setPreview(f);
  };
  const downloadKey = (key: string) => {
    const f = files.find((x) => x.key === key);
    saveFile(key, baseName(key), f?.httpMetadata?.contentType);
  };
  const [saveHint, setSaveHint] = useState<SaveEvent | null>(null);
  const [a2hs, setA2hs] = useState(false);
  useEffect(() => {
    const h = (e: any) => setSaveHint(e.detail);
    const t = (e: any) => setToast(e.detail);
    window.addEventListener("fd-save-hint", h);
    window.addEventListener("fd-toast", t);
    return () => { window.removeEventListener("fd-save-hint", h); window.removeEventListener("fd-toast", t); };
  }, []);
  useEffect(() => {
    if (isIOSSafari() && !isStandalone() && !localStorage.getItem("fd-a2hs-dismissed")) {
      const timer = setTimeout(() => setA2hs(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);
  const dismissA2hs = () => { localStorage.setItem("fd-a2hs-dismissed", "1"); setA2hs(false); };
  const startSelect = (k?: string) => { setSelectMode(true); if (k) setSelected((s) => new Set(s).add(k)); if (navigator.vibrate) navigator.vibrate(20); };
  const exitSelect = () => { setSelectMode(false); clearSel(); };
  const logout = async () => { try { await fetch("/api/logout", { method: "POST" }); } finally { onLogout(); } };

  const crumbs = cwd.replace(/\/$/, "").split("/").filter(Boolean);
  const isHome = crumbs.length === 0;
  const parentPath = crumbs.slice(0, -1).join("/");

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{ minHeight: "100vh" }}
        onDragEnter={(e) => { if (e.dataTransfer.types.includes("Files")) { dragDepth.current++; setDragging(true); } }}
        onDragLeave={() => { dragDepth.current = Math.max(0, dragDepth.current - 1); if (!dragDepth.current) setDragging(false); }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={async (e) => { e.preventDefault(); dragDepth.current = 0; setDragging(false); try { upload(await readDrop(e.dataTransfer)); } catch (err) { fail(err); } }}
      >
        <AppBar position="sticky" color="inherit" elevation={0} sx={{ pt: "env(safe-area-inset-top)", pl: "env(safe-area-inset-left)", pr: "env(safe-area-inset-right)", borderBottom: "1px solid #e5e7eb", bgcolor: "rgba(255,255,255,.9)", backdropFilter: "blur(8px)" }}>
          <Toolbar sx={{ gap: { xs: 1, sm: 2 }, minHeight: { xs: 52, sm: 64 }, px: { xs: 1.25, sm: 3 } }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ cursor: "pointer" }} onClick={() => setCwd("")}>
              <Cloud color="primary" sx={{ fontSize: { xs: 28, sm: 32 } }} />
              <Typography variant="h6" sx={{ fontWeight: 800, display: { xs: "none", sm: "block" } }}>我的网盘</Typography>
            </Stack>
            <TextField size="small" placeholder={isHome ? "搜索分类和文件" : "在当前分类中搜索"} value={search}
              onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, maxWidth: 480, ml: "auto", "& .MuiOutlinedInput-root": { bgcolor: "#f1f5f9", borderRadius: 99, height: { xs: 36, sm: 40 }, fontSize: { xs: 14, sm: 16 } } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize={mobile ? "small" : "medium"} /></InputAdornment> }} />
            {mobile ? (
              <>
                <IconButton aria-label="账户菜单" onClick={(e) => setAcctMenu(e.currentTarget)} edge="end"><MoreVert /></IconButton>
                <Menu anchorEl={acctMenu} open={!!acctMenu} onClose={() => setAcctMenu(null)}>
                  <MenuItem disabled><ListItemIcon><Person fontSize="small" /></ListItemIcon>{username}</MenuItem>
                  <MenuItem onClick={() => { setAcctMenu(null); load(); }}><ListItemIcon><Refresh fontSize="small" /></ListItemIcon>刷新</MenuItem>
                  <MenuItem onClick={() => { setAcctMenu(null); logout(); }}><ListItemIcon><Logout fontSize="small" /></ListItemIcon>退出登录</MenuItem>
                </Menu>
              </>
            ) : (
              <>
                <Tooltip title="刷新"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
                <Chip icon={<Person />} label={username} variant="outlined" sx={{ display: { xs: "none", md: "flex" } }} />
                <Button color="inherit" startIcon={<Logout />} onClick={logout} sx={{ flexShrink: 0 }}>退出登录</Button>
              </>
            )}
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ py: { xs: 1.5, sm: 3 }, pb: "calc(96px + env(safe-area-inset-bottom))", px: { xs: 1, sm: 3 }, pl: { xs: "max(8px, env(safe-area-inset-left))" }, pr: { xs: "max(8px, env(safe-area-inset-right))" } }}>
          <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: { xs: 1, sm: 2 }, fontSize: { xs: 13, sm: 16 }, "& button": { fontSize: { xs: 13, sm: 16 } }, display: { xs: isHome ? "none" : "block", sm: "block" } }} maxItems={mobile ? 3 : 8}>
            {isHome ? <Typography color="text.primary" sx={{ fontWeight: 600 }}>首页</Typography>
              : <Link component="button" underline="hover" onClick={() => setCwd("")} sx={{ fontSize: 16 }}>首页</Link>}
            {crumbs.map((c, i) => i === crumbs.length - 1
              ? <Typography key={i} color="text.primary" sx={{ fontWeight: 600 }}>{c}</Typography>
              : <Link key={i} component="button" underline="hover" onClick={() => setCwd(crumbs.slice(0, i + 1).join("/"))} sx={{ fontSize: 16 }}>{c}</Link>)}
          </Breadcrumbs>

          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: { xs: 1.5, sm: 3 } }}>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                {!isHome && !mobile && <Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center" }}><Folder color="primary" sx={{ fontSize: 30 }} /></Box>}
                <Typography variant="h4" noWrap sx={{ fontWeight: 800, fontSize: { xs: 22, sm: 34 } }}>{isHome ? "我的分类" : crumbs[crumbs.length - 1]}</Typography>
              </Stack>
              <Typography color="text.secondary" sx={{ fontSize: { xs: 12.5, sm: 16 } }}>
                {isHome ? (mobile ? "点击分类进入，右下角 + 上传" : "把文件按分类整理，点击分类卡片进入查看") : items ? `${folders.length ? folders.length + " 个文件夹 · " : ""}${files.length} 个文件 · 共 ${humanReadableSize(files.reduce((a, f) => a + f.size, 0))}` : "正在加载…"}
              </Typography>
            </Box>
            {mobile ? (
              visible.length > 0 && (selectMode
                ? <Button size="small" variant="outlined" onClick={exitSelect} sx={{ flexShrink: 0 }}>完成</Button>
                : <Button size="small" variant="outlined" startIcon={<Checklist />} onClick={() => startSelect()} sx={{ flexShrink: 0 }}>选择</Button>)
            ) : (
              <>
                {!isHome && <Button size="large" variant="outlined" startIcon={<ArrowBack />} onClick={() => setCwd(parentPath)}>返回</Button>}
                <Button size="large" variant="outlined" startIcon={<CreateNewFolder />} onClick={() => newFolder(isHome)}>{isHome ? "新建分类" : "新建文件夹"}</Button>
                <Button size="large" variant="outlined" startIcon={<DriveFolderUpload />} onClick={pickFolder}>上传文件夹</Button>
                <Button size="large" variant="contained" startIcon={<CloudUpload />} onClick={pickFiles}>上传文件</Button>
              </>
            )}
          </Stack>

          {mobile && selectMode && (
            <Paper elevation={6} sx={{ position: "fixed", left: 8, right: 8, bottom: "max(8px, env(safe-area-inset-bottom))", zIndex: 1250, px: 1.5, py: 1, display: "flex", alignItems: "center", gap: 0.5, borderRadius: 3 }}>
              <Typography sx={{ fontWeight: 700, flex: 1, fontSize: 14 }}>已选 {selected.size} 项</Typography>
              <Button size="small" onClick={() => (allSelected ? clearSel() : selectAll())}>{allSelected ? "取消全选" : "全选"}</Button>
              <Button size="small" variant="contained" color="error" startIcon={<Delete />} disabled={!selected.size} onClick={bulkDelete}>删除</Button>
            </Paper>
          )}
          {!mobile && visible.length > 0 && (
            <Paper elevation={selected.size ? 3 : 0} variant={selected.size ? "elevation" : "outlined"}
              sx={{ position: "sticky", top: 72, zIndex: 10, mb: 2, px: 2, py: 1, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap",
                bgcolor: selected.size ? "#eff6ff" : "#fff", borderColor: "#e5e7eb" }}>
              <Checkbox checked={allSelected} indeterminate={selected.size > 0 && !allSelected}
                onChange={() => (allSelected ? clearSel() : selectAll())} inputProps={{ "aria-label": "全选" }} />
              <Typography sx={{ fontWeight: 600, flex: 1 }}>
                {selected.size ? `已选 ${selected.size} 项` : "勾选卡片左上角的方框，可以一次选择多个进行删除"}
              </Typography>
              <Button startIcon={<SelectAll />} onClick={selectAll} disabled={allSelected}>全选</Button>
              {selected.size > 0 && <Button startIcon={<Deselect />} onClick={clearSel} color="inherit">取消选择</Button>}
              {selected.size > 0 && <Button variant="contained" color="error" startIcon={<DeleteSweep />} onClick={bulkDelete}>删除所选</Button>}
            </Paper>
          )}

          {loadError && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={load}>重试</Button>}>{loadError}</Alert>}

          {items === null ? (
            <Stack alignItems="center" sx={{ py: 10 }}><CircularProgress /><Typography sx={{ mt: 2 }} color="text.secondary">正在加载…</Typography></Stack>
          ) : (
            <Stack spacing={{ xs: 2.5, sm: 4 }}>
              {(isHome || folders.length > 0) && (
                <Box>
                  {!isHome && <Typography variant="h6" sx={{ fontWeight: 700, mb: { xs: 1, sm: 1.5 }, fontSize: { xs: 15, sm: 20 } }}>文件夹</Typography>}
                  {folders.length === 0 ? (
                    search ? <Typography color="text.secondary">没有找到匹配「{search}」的分类</Typography> :
                      <EmptyState title="还没有分类" hint="先建一个分类（比如“照片”“文档”），再把文件上传进去吧"
                        action={<Button variant="contained" size="large" startIcon={<Add />} onClick={() => newFolder(true)}>新建分类</Button>} />
                  ) : (
                    mobile ? (
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
                        {folders.map((f) => <MobileFolder key={f.key} item={f} selectMode={selectMode} selected={selected.has(f.key)} onToggle={() => toggle(f.key)}
                          onLongPress={() => startSelect(f.key)} onOpen={() => setCwd(f.key)} onRename={() => rename(f)} onDelete={() => remove(f)} />)}
                      </Box>
                    ) : <CardGrid min={200}>
                      {folders.map((f) => <CategoryCard key={f.key} item={f} selected={selected.has(f.key)} onToggle={() => toggle(f.key)} onOpen={() => setCwd(f.key)} onRename={() => rename(f)} onDelete={() => remove(f)} />)}
                    </CardGrid>
                  )}
                </Box>
              )}
              {(!isHome || files.length > 0) && (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: 15, sm: 20 } }}>{isHome ? "未分类的文件" : "文件"}</Typography>
                    <Chip size="small" label={`${files.length} 个`} />
                    <Box sx={{ flex: 1 }} />
                    {files.length > 0 && !mobile && (
                      <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
                        <ToggleButton value="grid" aria-label="网格视图"><ViewModule fontSize="small" sx={{ mr: 0.5 }} />网格</ToggleButton>
                        <ToggleButton value="list" aria-label="列表视图"><ViewList fontSize="small" sx={{ mr: 0.5 }} />列表</ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  </Stack>
                  {files.length === 0 ? (
                    search ? <Typography color="text.secondary">没有找到匹配「{search}」的文件</Typography> :
                      <EmptyState title={folders.length ? "这里还没有文件" : "这个分类还没有文件"} hint="点击下面的按钮上传，或者直接把文件 / 文件夹拖到这里"
                        action={<Button variant="contained" size="large" startIcon={<CloudUpload />} onClick={pickFiles}>上传文件</Button>} />
                  ) : (
                    mobile ? (
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "3px", mx: -0.5 }}>
                        {files.map((f) => <MobileTile key={f.key} item={f} selectMode={selectMode} selected={selected.has(f.key)} onToggle={() => toggle(f.key)}
                          onLongPress={() => startSelect(f.key)} onOpen={() => openFile(f)} onRename={() => rename(f)} onDelete={() => remove(f)} />)}
                      </Box>
                    ) : view === "grid" ? (
                      <CardGrid min={230}>
                        {files.map((f) => <FileCard key={f.key} item={f} selected={selected.has(f.key)} onToggle={() => toggle(f.key)} onOpen={() => openFile(f)} onRename={() => rename(f)} onDelete={() => remove(f)} />)}
                      </CardGrid>
                    ) : (
                      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                        {files.map((f) => <FileRow key={f.key} item={f} selected={selected.has(f.key)} onToggle={() => toggle(f.key)} onOpen={() => openFile(f)} onRename={() => rename(f)} onDelete={() => remove(f)} />)}
                      </Paper>
                    )
                  )}
                </Box>
              )}
              {isHome && !mobile && <Typography variant="body2" color="text.secondary" align="center">小提示：可以把文件或整个文件夹直接拖进浏览器窗口上传（在首页上传文件夹会自动变成一个新分类）；也可以用 WebDAV 地址 {window.location.origin}/webdav 在手机文件管理器里访问</Typography>}
            </Stack>
          )}
        </Container>

        {dragging && (
          <Box sx={{ position: "fixed", inset: 0, zIndex: 1400, bgcolor: "rgba(37,99,235,.12)", border: "4px dashed #2563eb", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
            <Paper sx={{ px: 5, py: 4, textAlign: "center" }} elevation={6}>
              <CloudUpload color="primary" sx={{ fontSize: 64 }} />
              <Typography variant="h5" sx={{ fontWeight: 800 }}>松开鼠标即可上传</Typography>
              <Typography color="text.secondary">文件将保存到「{isHome ? "首页（未分类）" : crumbs[crumbs.length - 1]}」</Typography>
            </Paper>
          </Box>
        )}

        <input ref={folderInput} type="file" hidden multiple {...({ webkitdirectory: "", directory: "" } as any)}
          onChange={(e) => { upload(Array.from(e.target.files || [])); e.target.value = ""; }} />
        <input ref={fileInput} type="file" multiple hidden onChange={(e) => { upload(Array.from(e.target.files || [])); e.target.value = ""; }} />
        <NameDialog open={!!nameDlg} title={nameDlg?.title || ""} label={nameDlg?.label || ""} initial={nameDlg?.initial}
          okText={nameDlg?.ok || "确定"} onClose={() => setNameDlg(null)} onSubmit={async (n) => { await nameDlg?.submit(n); }} />
        <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
        {mobile && !selectMode && (
          <Fab color="primary" aria-label="上传和新建" onClick={() => setSheet(true)}
            sx={{ position: "fixed", right: 18, bottom: "calc(20px + env(safe-area-inset-bottom))", zIndex: 1200 }}><Add /></Fab>
        )}
        <SwipeableDrawer anchor="bottom" open={sheet} onOpen={() => setSheet(true)} onClose={() => setSheet(false)} disableSwipeToOpen
          PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, pb: "env(safe-area-inset-bottom)" } }}>
          <Box sx={{ width: 40, height: 4, bgcolor: "#cbd5e1", borderRadius: 2, mx: "auto", mt: 1 }} />
          <Typography sx={{ fontWeight: 700, px: 2.5, pt: 1.5, pb: 0.5 }}>
            {isHome ? "添加到首页" : `添加到「${crumbs[crumbs.length - 1]}」`}
          </Typography>
          <List>
            {[
              { icon: <CloudUpload color="primary" />, t: "上传文件", d: "从相册或文件中选择，可多选", f: pickFiles },
              { icon: <DriveFolderUpload color="primary" />, t: "上传文件夹", d: "部分手机浏览器不支持", f: pickFolder },
              { icon: <CreateNewFolder color="primary" />, t: isHome ? "新建分类" : "新建文件夹", d: isHome ? "例如：照片、文档" : "在当前分类里再建一个文件夹", f: () => newFolder(isHome) },
            ].map((o) => (
              <ListItemButton key={o.t} onClick={() => { setSheet(false); o.f(); }} sx={{ py: 1.5, px: 2.5 }}>
                <ListItemIcon>{o.icon}</ListItemIcon>
                <ListItemText primary={o.t} secondary={o.d} primaryTypographyProps={{ fontWeight: 600 }} />
              </ListItemButton>
            ))}
          </List>
          <Button fullWidth color="inherit" onClick={() => setSheet(false)} sx={{ py: 1.5, borderTop: "1px solid #eef2f7", borderRadius: 0 }}>取消</Button>
        </SwipeableDrawer>
        {viewer !== null && viewerImages[viewer] && (
          <ImageViewer images={viewerImages} index={viewer} onIndex={setViewer} onClose={() => setViewer(null)} onDownload={(im) => downloadKey(im.key)} />
        )}
        <Dialog open={!!saveHint} onClose={() => setSaveHint(null)} fullWidth maxWidth="xs" sx={{ zIndex: 1600 }}>
          <DialogTitle sx={{ fontWeight: 700 }}>在微信里保存{saveHint?.isImage ? "图片" : "文件"}</DialogTitle>
          <DialogContent>
            {saveHint?.isImage ? (
              <DialogContentText component="div">
                <b>方法一：</b>打开图片后，<b>长按图片</b>，选择「保存图片」或「添加到相册」。<br /><br />
                <b>方法二：</b>点击右上角 <b>…</b> → 选择「<b>在浏览器打开</b>」（iPhone 选 Safari），在浏览器里点「保存到相册」。
              </DialogContentText>
            ) : (
              <DialogContentText component="div">
                微信内置浏览器不支持直接下载文件。<br />请点击右上角 <b>…</b> → 选择「<b>在浏览器打开</b>」，再点「下载」。
              </DialogContentText>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            {saveHint?.isImage && <Button onClick={() => { const u = saveHint.url; setSaveHint(null); window.open(u, "_blank"); }}>打开原图（长按保存）</Button>}
            <Button variant="contained" onClick={() => setSaveHint(null)}>知道了</Button>
          </DialogActions>
        </Dialog>
        {a2hs && (
          <Paper elevation={10} role="dialog" aria-label="添加到主屏幕提示" sx={{ position: "fixed", left: 10, right: 10, bottom: "calc(12px + env(safe-area-inset-bottom))", zIndex: 1450, p: 2, borderRadius: 3 }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box component="img" src="/apple-touch-icon.png" alt="" sx={{ width: 48, height: 48, borderRadius: 2.5, flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700 }}>把「我的网盘」添加到主屏幕</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.7 }}>
                  1. 点击 Safari 底部的分享按钮 <IosShare sx={{ fontSize: 16, verticalAlign: "-3px", color: "#2563eb" }} /><br />
                  2. 往下滑，选择「添加到主屏幕」<AddBoxOutlined sx={{ fontSize: 16, verticalAlign: "-3px", color: "#2563eb" }} /><br />
                  以后就能像 App 一样一键打开，不用再输网址
                </Typography>
              </Box>
              <IconButton size="small" aria-label="关闭提示" onClick={dismissA2hs} sx={{ mt: -0.5, mr: -0.5 }}><Close fontSize="small" /></IconButton>
            </Stack>
            <Button fullWidth variant="contained" onClick={dismissA2hs} sx={{ mt: 1.5 }}>知道了，不再提示</Button>
          </Paper>
        )}
        <PreviewDialog file={preview} onClose={() => setPreview(null)} />
        <Dialog open={!!bulk}><DialogContent sx={{ minWidth: 320 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>正在删除（{bulk?.done}/{bulk?.total}）…</Typography>
          <LinearProgress variant="determinate" value={bulk ? (bulk.done / bulk.total) * 100 : 0} />
        </DialogContent></Dialog>
        <UploadPanel tasks={tasks} onClear={() => setTasks([])} />
        <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
          <Alert severity={toast?.sev || "success"} variant="filled" onClose={() => setToast(null)}>{toast?.msg}</Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}

/* ---------- 登录 ---------- */
function LoginPage({ onLogin }: { onLogin: (u: string) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { document.title = "登录 - 我的网盘"; }, []);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) return setErr("请输入用户名和密码");
    setBusy(true); setErr("");
    try {
      const r = await fetch("/api/login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username, password }) });
      const d: any = await r.json().catch(() => ({}));
      if (r.ok && d.ok) onLogin(d.username);
      else setErr(d.error || `登录失败（${r.status}）`);
    } catch { setErr("网络连接失败，请稍后再试"); } finally { setBusy(false); }
  };
  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 2,
      background: "linear-gradient(135deg,#dbeafe 0%,#f5f7fb 50%,#ede9fe 100%)" }}>
      <Paper component="form" onSubmit={submit} elevation={6} sx={{ width: 400, maxWidth: "100%", p: 5, borderRadius: 4 }}>
        <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <Box sx={{ width: 72, height: 72, borderRadius: "50%", bgcolor: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Cloud color="primary" sx={{ fontSize: 44 }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>我的网盘</Typography>
          <Typography color="text.secondary">登录后即可查看和管理你的文件</Typography>
        </Stack>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Stack spacing={2}>
          <TextField label="用户名" autoFocus autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Person /></InputAdornment> }} />
          <TextField label="密码" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Lock /></InputAdornment> }} />
          <Button type="submit" variant="contained" size="large" disabled={busy} sx={{ py: 1.4, fontSize: 17 }}>
            {busy ? "正在登录…" : "登录"}
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" component="div" align="center" sx={{ mt: 3 }}>
          登录状态会保留 30 天，在公共电脑上用完请记得退出登录
        </Typography>
      </Paper>
    </Box>
  );
}

export default function App() {
  const [auth, setAuth] = useState<{ state: "checking" | "out" | "in"; username?: string }>({ state: "checking" });
  useEffect(() => {
    fetch("/api/me", { cache: "no-store" }).then((r) => r.json() as Promise<any>)
      .then((d) => setAuth(d.loggedIn ? { state: "in", username: d.username } : { state: "out" }))
      .catch(() => setAuth({ state: "out" }));
  }, []);
  useEffect(() => {
    // 会话过期时（WebDAV 返回 401）自动回到登录页
    const orig = window.fetch;
    (window as any).fetch = async (...args: any[]) => {
      const res = await (orig as any)(...args);
      const url = typeof args[0] === "string" ? args[0] : args[0] instanceof Request ? args[0].url : String(args[0]);
      if (res.status === 401 && url.includes("/webdav/")) setAuth({ state: "out" });
      return res;
    };
    return () => { window.fetch = orig; };
  }, []);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {auth.state === "checking" ? (
        <Stack alignItems="center" justifyContent="center" sx={{ height: "100vh" }}><CircularProgress /></Stack>
      ) : auth.state === "out" ? (
        <LoginPage onLogin={(u) => setAuth({ state: "in", username: u })} />
      ) : (
        <Drive username={auth.username || ""} onLogout={() => setAuth({ state: "out" })} />
      )}
    </ThemeProvider>
  );
}
